import React, {useMemo} from 'react';
import {Button, Spin, Text, TableColumnConfig, Label} from '@gravity-ui/uikit';
import {useNavigate} from 'react-router';
import {DataTable} from '../../shared';
import {CommunicationMessageModel, CommunicationGroup, CommunicationStatus, RecipientSelector} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {formatDate} from 'date-fns';
import styles from './CommunicationMessagesTable.module.scss';

interface CommunicationMessagesTableProps {
  messages: CommunicationMessageModel[] | undefined;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

const getGroupDisplayName = (group: CommunicationGroup): string => {
  const groupNames: Record<CommunicationGroup, string> = {
    ProjectNews: 'Новости проектов',
    AppealsAndReports: 'Сборы и отчеты',
    Ambassadors: 'Амбассадоры',
    TechnicalSupport: 'Техподдержка',
  };
  return groupNames[group];
};

const getStatusDisplayName = (status: CommunicationStatus): string => {
  const statusNames: Record<CommunicationStatus, string> = {
    Created: 'Создано',
    CollectingRecipients: 'Обработка получателей',
    DeliveringNotifications: 'В процессе',
    Processed: 'Отправлено',
    Failed: 'Ошибка',
  };
  return statusNames[status];
};

const getStatusColor = (
  status: CommunicationStatus,
): 'normal' | 'success' | 'danger' | 'info' | 'warning' | 'utility' | 'unknown' | 'clear' | undefined => {
  const statusColors: Record<
    CommunicationStatus,
    'normal' | 'success' | 'danger' | 'info' | 'warning' | 'utility' | 'unknown' | 'clear' | undefined
  > = {
    Created: 'normal',
    CollectingRecipients: 'normal',
    DeliveringNotifications: 'normal',
    Processed: 'success',
    Failed: 'danger',
  };
  return statusColors[status];
};

const getRecipientsSelectorDisplayName = (recipientsSelector: RecipientSelector): string => {
  const recipientsSelectorNames: Record<RecipientSelector, string> = {
    FromFile: 'Из файла',
    All: 'Все',
    Personal: 'Персональный',
    FromAppeal: 'Из сбора',
    NeverDonated: 'Не донатил ни разу',
    DonatedOnce: 'Донатили один раз',
    DonatedTwice: 'Донатили два раза',
    DonatedThreeOrMoreTimes: 'Донатили три и более раз',
    WithActiveSubscription: 'Есть действующая подписка',
    WithoutActiveSubscription: 'Нет действующей подписки',
    WithoutActiveButWithInactiveSubscription: 'Есть неактивная подписка',
  };
  return recipientsSelectorNames[recipientsSelector];
};

export const CommunicationMessagesTable = ({
  messages,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: CommunicationMessagesTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();

  const handleRowClick = (message: CommunicationMessageModel) => {
    navigate(`/notifications/messages/${message.id}`);
  };

  const columns = useMemo<TableColumnConfig<CommunicationMessageModel>[]>(
    () => [
      {
        id: 'createdAt',
        name: 'Дата создания',
        template: (item: CommunicationMessageModel) => (
          <Text variant="body-1">{formatDate(new Date(item.createdAt), 'dd.MM.yyyy HH:mm')}</Text>
        ),
      },
      {
        id: 'subject',
        name: 'Заголовок',
        template: (item: CommunicationMessageModel) => (
          <Text variant="body-1" style={{fontWeight: '500'}} className={styles.subjectCell}>
            {item.subject}
          </Text>
        ),
      },
      {
        id: 'recipientsSelector',
        name: 'Получатели',
        template: (item: CommunicationMessageModel) => (
          <Text variant="body-1">{getRecipientsSelectorDisplayName(item.recipientsSelector)}</Text>
        ),
      },
      {
        id: 'group',
        name: 'Группа',
        template: (item: CommunicationMessageModel) => <Text variant="body-1">{getGroupDisplayName(item.group)}</Text>,
      },
      {
        id: 'status',
        name: 'Статус',
        template: (item: CommunicationMessageModel) => (
          <Label theme={getStatusColor(item.status)}>{getStatusDisplayName(item.status)}</Label>
        ),
      },
      {
        id: 'recipientsCount',
        name: 'Количество получателей',
        template: (item: CommunicationMessageModel) => (
          <Text variant="body-1">{item.recipientsCount.toLocaleString()}</Text>
        ),
      },
      {
        id: 'deliveredNotificationsCount',
        name: 'Доставлено сообщений',
        template: (item: CommunicationMessageModel) => (
          <Text variant="body-1">{item.deliveredNotificationsCount.toLocaleString()}</Text>
        ),
      },
      {
        id: 'readCount',
        name: 'Прочитано',
        template: (item: CommunicationMessageModel) => <Text variant="body-1">{item.readCount.toLocaleString()}</Text>,
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<CommunicationMessageModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={messages || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Сообщения не найдены"
        emptyStateDescription="Сообщения с таким названием не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
